import React, { useState, useEffect, useCallback } from 'react';
import { Card, Input, Row, Col, Empty, List, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { searchProducts } from '../../utils/productsSearch';
import VirtualKeyboard from '../Sale/Keyboard'; // Импортируй компонент клавиатуры

const { Text } = Typography;
const COLOR_MAP = {
    'lightcoral': 'rgb(255, 178, 169)',
    'peachpuff': 'rgb(255, 193, 125)',
    'khaki': 'rgb(253, 212, 129)',
    'lightgreen': 'rgb(135, 227, 154)',
    'lightskyblue': 'rgb(143, 205, 255)',
    'cornflowerblue': 'rgb(159, 184, 255)',
    'plum': 'rgb(222, 163, 236)',
    'lightgray': 'var(--ant-color-fill-tertiary)'
};

const QuickProducts = ({ onAddToCart, searchQuery, onSearchChange, searchResults, onAddToCartAndClearSearch }) => {
    const [quickProducts, setQuickProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);

    // --- Новое состояние для виртуальной клавиатуры ---
    const [showKeyboard, setShowKeyboard] = useState(false);

    // Загрузка быстрых товаров
    const loadQuickProducts = useCallback(async () => {
        try {
            const deviceInfo = JSON.parse(localStorage.getItem('device_register_info') || '{}');
            const quickProductsConfig = deviceInfo.cash_register?.quick_products || [];

            const loadedProducts = [];
            const loadedCategories = [];

            for (const group of quickProductsConfig) {
                const groupProducts = [];

                for (const productRef of group.products) {
                    const products = await searchProducts(productRef.id.toString(), 'id', 1);
                    if (products.length > 0) {
                        groupProducts.push({
                            ...products[0],
                            displayName: productRef.name || products[0].name,
                            backgroundColor: COLOR_MAP[productRef.color] || COLOR_MAP['lightgray']
                        });
                    }
                }

                if (groupProducts.length > 0) {
                    loadedProducts.push(...groupProducts.map(p => ({
                        ...p,
                        category: group.group_name || 'Без категории'
                    })));

                    if (group.group_name && !loadedCategories.includes(group.group_name)) {
                        loadedCategories.push(group.group_name);
                    }
                }
            }

            setQuickProducts(loadedProducts);
            setCategories(loadedCategories);

            if (loadedCategories.length > 0) {
                setSelectedCategory(loadedCategories[0]);
            }
        } catch (error) {
            console.error('Ошибка загрузки быстрых товаров:', error);
        }
    }, []);

    useEffect(() => {
        loadQuickProducts();
    }, [loadQuickProducts]);

    useEffect(() => {
        if (searchQuery === '') {
            setShowKeyboard(false);
        }
    }, [searchQuery]);

    const filteredProducts = quickProducts.filter(product => {
        const matchesCategory = !selectedCategory || product.category === selectedCategory;
        return matchesCategory;
    });

    // --- Обработка нажатий виртуальной клавиатуры ---
    const handleVirtualKeyPress = (key) => {
        if (key === 'Backspace') {
            onSearchChange({ target: { value: searchQuery.slice(0, -1) } });
        } else {
            onSearchChange({ target: { value: searchQuery + key } });
        }
    };

    const handleProductSelect = (product) => {
        onAddToCartAndClearSearch(product);
        setShowKeyboard(false);
    };

    return (
        <>
            <Card
                bordered={false}
                style={{ height: '100%', position: 'relative' }}
                bodyStyle={{
                    height: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    userSelect: 'none'
                }}
            >
                {/* Поиск */}
                <Input
                    placeholder="Поиск товаров..."
                    prefix={<SearchOutlined />}
                    value={searchQuery}
                    onChange={onSearchChange}
                    onFocus={() => setShowKeyboard(true)} // Показываем виртуальную клавиатуру
                    style={{ marginBottom: 16 }}
                    size="large"
                />

                {/* Результаты поиска */}
                {searchResults.length > 0 && (
                    <div style={{
                        position: 'absolute',
                        top: '70px',
                        left: '24px',
                        right: '24px',
                        backgroundColor: 'var(--ant-color-bg-elevated)',
                        border: '1px solid var(--ant-color-border)',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        maxHeight: '380px',
                        overflow: 'auto'
                    }}>
                        <div style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid var(--ant-color-split)'
                        }}>
                            <Text strong style={{
                                fontSize: '14px',
                                color: 'var(--ant-color-text-secondary)'
                            }}>
                                Результаты поиска
                            </Text>
                        </div>
                        <List
                            size="small"
                            dataSource={searchResults}
                            renderItem={(product) => (
                                <List.Item
                                    onClick={() => handleProductSelect(product)}
                                    style={{
                                        cursor: 'pointer',
                                        padding: '12px 16px',
                                        margin: 0,
                                        transition: 'background-color 0.2s',
                                        borderBottom: '1px solid var(--ant-color-split)'
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                        alignItems: 'center'
                                    }}>
                                        <div style={{ flex: 1, marginRight: '12px' }}>
                                            <Text style={{
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                color: 'var(--ant-color-text)'
                                            }}>
                                                {product.name}
                                            </Text>
                                        </div>
                                        <Text strong style={{
                                            fontSize: '14px',
                                            color: 'var(--ant-color-text)'
                                        }}>
                                            {product.price} ₸
                                        </Text>
                                    </div>
                                </List.Item>
                            )}
                        />
                    </div>
                )}

                {/* Категории */}
                <div style={{
                    marginBottom: 16,
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                    paddingBottom: 8,
                }}>
                    <Space size={24} style={{ display: 'inline-flex' }}>
                        {categories.map(category => (
                            <Text
                                strong
                                key={category}
                                type={selectedCategory === category ? undefined : 'secondary'}
                                style={{
                                    fontSize: 20,
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    userSelect: 'none'
                                }}
                                onClick={() => setSelectedCategory(category)}
                            >
                                {category}
                            </Text>
                        ))}
                    </Space>
                </div>

                {/* Сетка товаров */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    overflowX: 'hidden'
                }}>
                    <Row gutter={[12, 12]}>
                        {filteredProducts.map(product => (
                            <Col span={4} key={product.id} style={{ minWidth: '20%' }}>
                                <div
                                    onClick={() => onAddToCart(product)}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: product.backgroundColor,
                                        borderRadius: 12,
                                        padding: 16,
                                        width: '100%',
                                        aspectRatio: '1',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <div style={{
                                        fontSize: 18,
                                        fontWeight: 500,
                                        marginBottom: 8,
                                        overflow: 'hidden',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        textAlign: 'left',
                                        color: 'var(--ant-color-text)'
                                    }}>
                                        {product.displayName}
                                    </div>
                                    <div style={{
                                        fontSize: 16,
                                        color: 'var(--ant-color-text-secondary)',
                                        textAlign: 'left'
                                    }}>
                                        {product.price} ₸
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>

                    {filteredProducts.length === 0 && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '200px'
                        }}>
                            <Empty description="Товары не найдены" />
                        </div>
                    )}
                </div>
            </Card>

            {/* Подключение виртуальной клавиатуры */}
            <VirtualKeyboard
                visible={showKeyboard}
                onKeyPress={handleVirtualKeyPress}
                onClose={() => setShowKeyboard(false)}
            />
        </>
    );
};

export default QuickProducts;
